// Vue Composition API directive for skeleton loading elements
import { useMediaQueries } from '~coreModules/core/js/composables/useMediaQueries';

import { BREAK_POINT_SIZES } from '~coreModules/core/js/constants';
import { TRAY_TITLE, TRAY_SUBTITLE, TRAY_CAPTION } from '~coreModules/core/js/skeleton-constants';

/**
 * The `skeletonTypes` object defines the available skeleton loading shapes and their dimensions
 * for different breakpoints. Each key in `skeletonTypes` should be a unique type constant
 * (e.g., TRAY_TITLE, TRAY_SUBTITLE, TRAY_CAPTION).
 *
 * For each type, define one or more breakpoint-specific objects using the breakpoint constants
 * from BREAK_POINT_SIZES (e.g., EXTRA_SMALL, SMALL, MEDIUM, LARGE, EXTRA_LARGE).
 * Each breakpoint object should specify a `width` and `height` as strings (e.g., '280px').
 *
 * Example:
 *
 * const skeletonTypes = {
 *   [TRAY_TITLE]: {
 *     [BREAK_POINT_SIZES.EXTRA_SMALL]: {
 *       width: '280px',
 *       height: '24px',
 *     },
 *     [BREAK_POINT_SIZES.MEDIUM]: {
 *       width: '400px',
 *       height: '20px',
 *     },
 *   },
 *   [TRAY_SUBTITLE]: {
 *     [BREAK_POINT_SIZES.EXTRA_SMALL]: {
 *       width: '216px',
 *       height: '24px',
 *     },
 *     [BREAK_POINT_SIZES.MEDIUM]: {
 *       width: '280px',
 *       height: '20px',
 *     },
 *   },
 *   // Add new types below:
 *   [MY_NEW_TYPE]: {
 *     [BREAK_POINT_SIZES.SMALL]: {
 *       width: '150px',
 *       height: '18px',
 *     },
 *     [BREAK_POINT_SIZES.LARGE]: {
 *       width: '300px',
 *       height: '22px',
 *     },
 *   },
 * };
 *
 * Notes:
 * - You do not need to define every breakpoint for every type. If a breakpoint is missing,
 *   the directive will fall back to the next available smaller size.
 * - Use the constants from BREAK_POINT_SIZES for breakpoint keys to ensure consistency.
 * - Use string values with units (e.g., 'px', 'em') for width and height.
 */
const skeletonTypes = {
    [TRAY_TITLE]: {
        [BREAK_POINT_SIZES.EXTRA_SMALL]: {
            width: '280px',
            height: '24px',
        },
        [BREAK_POINT_SIZES.MEDIUM]: {
            width: '400px',
            height: '20px',
        },
    },
    [TRAY_SUBTITLE]: {
        [BREAK_POINT_SIZES.EXTRA_SMALL]: {
            width: '216px',
            height: '24px',
        },
        [BREAK_POINT_SIZES.MEDIUM]: {
            width: '280px',
            height: '20px',
        },
    },
    [TRAY_CAPTION]: {
        [BREAK_POINT_SIZES.EXTRA_SMALL]: {
            width: '56px',
            height: '20px',
        },
    },
};

/**
 * Returns the dimensions (width and height) for a given skeleton type and breakpoint.
 * If the exact breakpoint is not defined for the type, it falls back to the next available
 * smaller size in the order: extra-small, small, medium, large, extra-large.
 *
 * @param {string} currentType - The skeleton type (e.g., 'Title', 'Subtitle', 'Caption').
 * @param {string} currentBreakpoint - The current breakpoint (e.g., 'medium', 'small').
 * @returns {{ width: string, height: string }} The width and height for the skeleton.
 */
function getDimensionsForType(currentType, currentBreakpoint) {
    const { EXTRA_SMALL, SMALL, MEDIUM, LARGE, EXTRA_LARGE } = BREAK_POINT_SIZES;

    const typeConfig = skeletonTypes[currentType] || {};

    const sizeOrder = [EXTRA_SMALL, SMALL, MEDIUM, LARGE, EXTRA_LARGE];

    let matchedConfig;
    if (typeConfig[currentBreakpoint]) {
        matchedConfig = typeConfig[currentBreakpoint];
    } else {
        const currentSizeIndex = sizeOrder.findIndex(size => size === currentBreakpoint);

        for (let i = currentSizeIndex; i >= 0; i--) {
            const size = sizeOrder[i];

            if (typeConfig[size]) {
                matchedConfig = typeConfig[size];
                break;
            }
        }
    }

    return matchedConfig;
}

/**
 * v-skeleton directive
 * Update styling based on directive binding value
 *
 * @param {HTMLElement} el - The element the directive is bound to
 * @param {Object} value - The value passed to the directive
 * @param {Boolean} value.loading - Whether the element is in loading state
  * @param {String} value.height - Height of the skeleton (determined by value.type if omitted)
 * @param {String} value.width - Width of the skeleton (determined by value.type if omitted)
 */
function updateStyling(el, value) {
    const { currentBreakpoint } = useMediaQueries();

    // Get the dimensions for the current type and screen size
    const dimensions = getDimensionsForType(value?.type, currentBreakpoint);

    // Default values
    const options = {
        loading: false,
        height: `${dimensions.height}`,
        width: `${dimensions.width}`,
        ...value,
    };

    el.classList.add('c-skeleton');

    if (options.loading) {
        el.classList.add('c-skeleton');
        el.style.setProperty('--skeleton-width', options.width);
        el.style.setProperty('--skeleton-height', options.height);
    } else {
        if (!el.innerHTML.trim() && !el.textContent.trim()) {
            /* eslint-disable no-param-reassign */
            el.style.visibility = 'hidden';
        }
        el.classList.remove('c-skeleton');
        el.style.removeProperty('--skeleton-width');
        el.style.removeProperty('--skeleton-height');
    }
}

/**
 * v-skeleton directive
 *
 * Usage:
 * v-skeleton="{ loading: true, height: '24px', width: '280px' }"
 *
 * @type {import('vue').Directive}
 */
export const vSkeleton = {
    created(el, binding) {
        updateStyling(el, binding.value);
    },
    updated(el, binding) {
        updateStyling(el, binding.value);
    },
};

export default vSkeleton;
